/**
 * The Attractors lesson's two figures (Coleman's doc, 2026-10-12), drawn
 * here as our own conceptual sketches: the doc names a PNG for each, and
 * asks that they stay original and conceptual, never a copied research
 * figure or anything that reads as measured data.
 *
 *   landscapes  three landscapes: one very deep valley, almost no clear
 *               valleys, several accessible valleys (core)
 *   addShift    the bifurcation route (another valley appears) beside the
 *               shift route (the valley moves), dashed before, solid after
 *               (Certification)
 *
 * The labels are the doc's own words; the caption under each is his
 * illustration note, carried on the block and checked by the verbatim test.
 */
import React from "react";
import { GOLD } from "../constants/theme";

const LANDSCAPES = [
  { label: "One very deep valley",
    d: "M4 22 C 40 22, 52 92, 80 92 C 108 92, 120 22, 156 22", ball: [80, 85] },
  { label: "Almost no clear valleys",
    d: "M4 60 C 24 56, 40 64, 60 60 C 80 56, 100 64, 120 60 C 136 57, 148 61, 156 60", ball: [60, 53] },
  { label: "Several accessible valleys",
    d: "M4 32 C 14 32, 20 66, 30 66 C 40 66, 46 40, 55 40 C 64 40, 70 68, 80 68 C 90 68, 96 40, 105 40 C 114 40, 120 66, 130 66 C 140 66, 146 32, 156 32",
    ball: [80, 61] },
];

const ROUTES = [
  { label: "Bifurcation route",
    before: "M6 30 C 40 30, 50 80, 70 80 C 90 80, 110 30, 194 30",
    after: "M6 30 C 40 30, 50 80, 70 80 C 90 80, 96 44, 105 44 C 114 44, 122 78, 140 78 C 158 78, 166 30, 194 30",
    ball: [140, 71] },
  { label: "Shift route",
    before: "M6 30 C 40 30, 50 80, 70 80 C 90 80, 110 30, 194 30",
    after: "M6 30 C 70 30, 110 80, 130 80 C 150 80, 160 30, 194 30",
    ball: [130, 73], arrow: true },
];

function Panel({ C, label, children, viewBox }) {
  return (
    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      <svg viewBox={viewBox} width="100%" aria-hidden="true" focusable="false"
        style={{ display: "block", overflow: "visible" }}>{children}</svg>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.textMid, lineHeight: 1.35, textAlign: "center" }}>{label}</div>
    </div>
  );
}

export function ConceptGraphic({ C, name, alt, caption }) {
  const ink = C.textMid;
  const ground = C.text;
  let panels = null;
  if (name === "landscapes") {
    panels = LANDSCAPES.map(p => (
      <Panel key={p.label} C={C} label={p.label} viewBox="0 0 160 100">
        <path d={`${p.d} L156 100 L4 100 Z`} fill={ground} opacity={0.05}/>
        <path d={p.d} fill="none" stroke={ink} strokeWidth={2} strokeLinecap="round"/>
        <circle cx={p.ball[0]} cy={p.ball[1]} r={6} fill={GOLD}/>
      </Panel>
    ));
  } else if (name === "addShift") {
    panels = ROUTES.map(p => (
      <Panel key={p.label} C={C} label={p.label} viewBox="0 0 200 110">
        <path d={p.before} fill="none" stroke={ink} strokeWidth={1.5} strokeDasharray="4 4" opacity={0.6}/>
        <path d={p.after} fill="none" stroke={ink} strokeWidth={2} strokeLinecap="round"/>
        {p.arrow && (
          <g stroke={GOLD} strokeWidth={1.6} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M78 98 H 120"/>
            <path d="M114 93 L 121 98 L 114 103"/>
          </g>
        )}
        <circle cx={p.ball[0]} cy={p.ball[1]} r={6} fill={GOLD}/>
      </Panel>
    ));
  }
  if (!panels) return null;
  return (
    <figure role="img" aria-label={alt} style={{ margin: "8px 0 24px" }}>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${panels.length}, minmax(0, 1fr))`,
        gap: name === "landscapes" ? 14 : 22, padding: "18px 16px 14px", borderRadius: 12,
        border: `1px solid ${C.border}`, background: C.bgCard || "transparent" }}>
        {panels}
      </div>
      {caption && (
        <figcaption style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.55, color: C.textDim }}>{caption}</figcaption>
      )}
    </figure>
  );
}

export default ConceptGraphic;
